// External libraries
import { hasOwnProp, isFunction } from '@checkout/react-components';
import { PAYPAL as ULInject } from 'ul-inline/dist/inject';

// Internal modules
import { $logger } from '@/lib/logger';
import { getGlobals, getLocale } from '@/lib';

/**
 * @function dropOneTouchCookie
 * @param {string} token Checkout token
 * @returns
 * @description injects an iframe from unified login onto the page in order
 * to exchange id_token for refresh_token in case of one touch
 */
const dropOneTouchCookie = (token, timeoutValue = 10 * 1000) => {
  return new Promise((resolve, reject) => {
    let timeoutPromise;

    const startTimer = () => {
      timeoutPromise = setTimeout(() => {
        reject('one_touch_timeout');
      }, timeoutValue);
    };

    const failureHandler = err => {
      clearTimeout(timeoutPromise);
      const message = err?.message || err;
      reject(message || 'one_touch_error');
    };

    const ulEventHandlers = {
      oneTouchInterstitialOptInFailed: err => failureHandler(err),
      oneTouchInterstitialOptInNotAuthorized: err => {
        // Removing the ulTarget element from DOM to avoid any other accidental
        // actions on the element
        document.getElementById('ulTarget').remove();
        failureHandler(err);
      },
      oneTouchInterstitialOptInSuccess: () => {
        clearTimeout(timeoutPromise);
        $logger.info('one_touch_success');
        resolve();
      },

      // Don't do anything for these events - maybe we should just remove it
      oneTouchInterstitialOptInAttempted: () => {}, // eslint-disable-line sort-keys
      oneTouchInterstitialOptInLoaded: () => {},
    };

    const postMessageHandler = response => {
      $logger.info('one_touch_post_response', response);
      const { event } = response.data || response.error;

      if (event && hasOwnProp(ulEventHandlers, event)) {
        const handler = ulEventHandlers[event];
        isFunction(handler) && handler(event);
      }
    };

    // Loads an iframe from unified login that drops the POMA cookie
    const ulTargetElement = document.createElement('div');
    ulTargetElement.setAttribute('id', 'ulTarget');

    // Hiding the iframe
    ulTargetElement.style.height = '0px';
    ulTargetElement.style.width = '0px';
    ulTargetElement.style.margin = '0px';
    ulTargetElement.style.padding = '0px';

    document.body.appendChild(ulTargetElement);

    const { country, locale } = getLocale();

    const options = {
      iframePathname: '/signin/activate-one-touch',
      onPostMessage: postMessageHandler,
      origin: getGlobals().uri.base,
      queryParams: {
        correlationId: getGlobals().correlationId,
        'country.x': country,
        flowId: token,
        flowName: 'checkoutOnlyMember', // default is poma flow
        'locale.x': locale,
      },
      targetElement: ulTargetElement,
    };

    // Timer is used to throw an error if we don't get a response from UL
    startTimer();
    ULInject.unifiedLogin.inject(options);
  });
};

/**
 * @function pomaLogout
 * @returns {Promise}
 * @description logouts for poma flow and resolves the promise with
 * `true` if it was a success or `false` if it wasn't
 */
const pomaLogout = () => {
  return fetch('/checkoutweb/poma-logout')
    .then(() => {
      $logger.info('poma_logout_success');
      return true;
    })
    .catch(({ message }) => {
      $logger.error('poma_logout_error', { message });
      return false;
    });
};

export { dropOneTouchCookie, pomaLogout };
